import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Spinner,
  Card,
  CardHeader,
  Dropdown,
  Option,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import {
  listMatchReviewRows,
  confirmMatchReview,
  skipMatchReview,
} from '../../services/api';
import type { MatchReviewRow, ContactSearchResult } from '../../types';
import BootstrapDialog from '../../components/admin/BootstrapDialog';
import ContactSearchBox from '../../components/admin/ContactSearchBox';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const YEAR_OPTIONS = [2024, 2023];

export default function MatchReviewPage() {
  const qc = useQueryClient();
  const [taxYear, setTaxYear] = useState<number>(YEAR_OPTIONS[0]);
  const [pageSize, setPageSize] = useState<number>(50);
  const [skip, setSkip] = useState<number>(0);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [bootstrapOpen, setBootstrapOpen] = useState(false);
  const [extraCandidates, setExtraCandidates] = useState<Record<string, ContactSearchResult>>({});

  const pageQuery = useQuery({
    queryKey: ['match-review', taxYear, pageSize, skip],
    queryFn: () => listMatchReviewRows({ taxYear, take: pageSize, skip }),
    placeholderData: keepPreviousData,
  });

  const invalidateAll = () => qc.invalidateQueries({ queryKey: ['match-review'] });

  const confirmMutation = useMutation({
    mutationFn: ({ row, contactId }: { row: MatchReviewRow; contactId: string }) =>
      confirmMatchReview(row.id, contactId, row.taxYear),
    onSuccess: invalidateAll,
  });

  const skipMutation = useMutation({
    mutationFn: (row: MatchReviewRow) => skipMatchReview(row.id, row.taxYear),
    onSuccess: invalidateAll,
  });

  const changeYear = (year: number) => {
    setTaxYear(year);
    setSkip(0);
    setSelected({});
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setSkip(0);
  };

  const data = pageQuery.data;
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const nextSkip = data?.nextSkip ?? null;
  const prevSkip = Math.max(0, skip - pageSize);
  const pageEnd = Math.min(skip + rows.length, total);

  return (
    <div style={{ padding: 24 }}>
      <h1>Match Review (Admin)</h1>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0 16px' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {YEAR_OPTIONS.map(y => (
            <Button
              key={y}
              appearance={y === taxYear ? 'primary' : 'secondary'}
              onClick={() => changeYear(y)}
            >
              {y}
            </Button>
          ))}
        </div>

        <Dropdown
          aria-label="Page size"
          value={`${pageSize} per page`}
          selectedOptions={[String(pageSize)]}
          onOptionSelect={(_, d) => {
            const next = Number(d.optionValue);
            if (Number.isFinite(next) && next > 0) changePageSize(next);
          }}
          style={{ minWidth: 140 }}
        >
          {PAGE_SIZE_OPTIONS.map(n => (
            <Option key={n} value={String(n)}>{`${n} per page`}</Option>
          ))}
        </Dropdown>

        <Button onClick={() => setBootstrapOpen(true)}>Bootstrap…</Button>

        {pageQuery.isFetching && !pageQuery.isLoading && <Spinner size="tiny" label="Refreshing..." />}
      </div>

      {pageQuery.isLoading ? (
        <Spinner label="Loading..." />
      ) : pageQuery.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load match review rows.</MessageBarTitle>
            {pageQuery.error instanceof Error ? pageQuery.error.message : 'Unknown error'}
            <div style={{ marginTop: 8 }}>
              <Button onClick={() => pageQuery.refetch()}>Retry</Button>
            </div>
          </MessageBarBody>
        </MessageBar>
      ) : (
        <>
          <p>
            Showing {rows.length === 0 ? 0 : skip + 1}–{pageEnd} of {total} pending rows for {taxYear}.
          </p>

          {rows.length === 0 && <p><em>No pending rows on this page.</em></p>}

          {rows.map(row => (
            <Card key={row.id} style={{ margin: '12px 0', padding: 16 }}>
              <CardHeader
                header={
                  <strong>
                    {row.clientNameRaw} &mdash; {row.returnType} &mdash; {row.taxYear} ({row.rawStatus})
                  </strong>
                }
                description={`Last 4: ${row.last4}`}
              />
              <div style={{ marginTop: 12 }}>
                {row.candidates.length === 0 && <em>No candidates found.</em>}
                {row.candidates.map(c => (
                  <label key={c.id} style={{ display: 'block', padding: 4 }}>
                    <input
                      type="radio"
                      name={`row-${row.id}`}
                      checked={selected[row.id] === c.id}
                      onChange={() => setSelected(s => ({ ...s, [row.id]: c.id }))}
                    />{' '}
                    {c.fullName} &mdash; {c.last4Matches ? '✓ last-4 matches' : '✗ last-4 does not match'}
                    {c.email && <> &mdash; {c.email}</>}
                    {c.phone && <> &mdash; {c.phone}</>}
                  </label>
                ))}
                <ContactSearchBox
                  rowId={row.id}
                  onSelect={(id, candidate) => {
                    setExtraCandidates(prev => ({ ...prev, [row.id]: candidate }));
                    setSelected(s => ({ ...s, [row.id]: id }));
                  }}
                />
                {extraCandidates[row.id] && selected[row.id] === extraCandidates[row.id].id && (
                  <p style={{ marginTop: 4, fontSize: 12, color: '#444' }}>
                    Will confirm: <strong>{extraCandidates[row.id].fullName}</strong>
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button
                  appearance="primary"
                  disabled={!selected[row.id] || confirmMutation.isPending}
                  onClick={() => {
                    if (selected[row.id]) confirmMutation.mutate({ row, contactId: selected[row.id] });
                  }}
                >
                  Confirm
                </Button>
                <Button
                  appearance="secondary"
                  disabled={skipMutation.isPending}
                  onClick={() => skipMutation.mutate(row)}
                >
                  Skip
                </Button>
              </div>
            </Card>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <Button
              disabled={skip === 0 || pageQuery.isFetching}
              onClick={() => setSkip(prevSkip)}
            >
              Previous
            </Button>
            <Button
              disabled={nextSkip == null || pageQuery.isFetching}
              onClick={() => nextSkip != null && setSkip(nextSkip)}
            >
              Next
            </Button>
          </div>
        </>
      )}

      <BootstrapDialog
        open={bootstrapOpen}
        onClose={() => setBootstrapOpen(false)}
        onCommitted={() => qc.invalidateQueries({ queryKey: ['match-review'] })}
      />
    </div>
  );
}
